/**
 * RESPONSIBILITY: pick up `?flag=` from the URL and enable those flags.
 *
 *   /?flag=4              anywhere on the site
 *   /?flag=0b0110         binary
 *   /#/profile?flag=12    after the hash, on a screen route
 *
 * Works on ANY page — it reads the location, not a route. ENABLE-ONLY: a clear bit
 * is not an instruction to switch anything off, so a link can only ever add to what
 * the recipient already has.
 *
 * The parameter is stripped from the address bar afterwards, so a copied URL doesn't
 * silently re-enable things for the next person.
 */
import { enableFlagCode } from './store';
import { isValidCode, describeCode } from './flag-code';

export const FLAG_PARAM = 'flag';

/** Looks in the query string AND after the hash, so it works on hash routes too. */
function readParam(): string | null {
  const { search, hash } = window.location;

  const fromSearch = new URLSearchParams(search).get(FLAG_PARAM);
  if (fromSearch) return fromSearch;

  const q = hash.indexOf('?');
  if (q === -1) return null;
  return new URLSearchParams(hash.slice(q + 1)).get(FLAG_PARAM);
}

function stripParam(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete(FLAG_PARAM);

  const q = url.hash.indexOf('?');
  if (q !== -1) {
    const params = new URLSearchParams(url.hash.slice(q + 1));
    params.delete(FLAG_PARAM);
    const rest = params.toString();
    url.hash = url.hash.slice(0, q) + (rest ? `?${rest}` : '');
  }

  window.history.replaceState(null, '', url.toString());
}

/** Call once at startup, before rendering. Returns the keys it turned on. */
export function applyFlagsFromUrl(): string[] {
  if (typeof window === 'undefined') return [];

  const raw = readParam();
  if (!raw) return [];

  // Unknown characters are reported, not fatal — the rest still apply.
  if (!isValidCode(raw)) {
    console.warn(`[flags] ?${FLAG_PARAM}=${raw} — ${describeCode(raw)}`);
  }

  const turnedOn = enableFlagCode(raw);
  stripParam();

  if (turnedOn.length) console.info(`[flags] enabled from URL: ${turnedOn.join(', ')}`);
  return turnedOn;
}

/**
 * Keep reading it, because on a hash-routed site the URL changes without a page load.
 *
 * applyFlagsFromUrl runs once before React mounts. That is right for arriving at a flag
 * link and wrong for everything else: paste `#home?flag=A` into a tab that is already
 * open and the hash changes, the router routes, and nothing reads the parameter — the
 * link silently does nothing and the only way to make it work is a reload nobody was
 * told about. A flag link should work whichever way you arrive at it.
 *
 * The store notifies its subscribers, so the page changes under the person immediately.
 *
 * Returns the unsubscribe, so whoever starts it can stop it.
 */
export function watchFlagsInUrl(): () => void {
  if (typeof window === 'undefined') return () => {};

  const onChange = () => { applyFlagsFromUrl(); };
  window.addEventListener('hashchange', onChange);
  return () => window.removeEventListener('hashchange', onChange);
}
